import React, { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useForm, Controller } from "react-hook-form";
import { useMediaQuery } from "@mui/material";
import equal from "fast-deep-equal";
import { makeStyles } from "tss-react/mui";
import "moment-timezone";
import { setObjectFieldByPath } from "../../utils/object.js";
import "react-intl";
import { FilterGroupButtons } from "./FilterGroupButtons.js";
import { FiltersContainer } from "./FiltersContainer.js";
import { MultiStepFilterDrawer } from "./MultiStepFilterDrawer.js";
const useStyles = makeStyles()((theme) => {
  return {
    filterGroupRootContainer: {
      width: "100%"
    },
    filterGroupContainerFilter: {
      [theme.breakpoints.down("md")]: {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto"
      }
    },
    filterGroupContainerFilterHidden: {
      display: "none"
    }
  };
});
function FilterGroupNew({
  availableFilters,
  allFilters,
  model,
  mode,
  onChange,
  groupContainerFullWidth = true,
  viewMode = "flex",
  withResetButton,
  isDataLoading,
  defaultFilter
}) {
  const { classes, cx, theme } = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxVisibleItemsCountDesktop = 12;
  const [expanded, setExpanded] = useState(false);
  const form = useForm({ defaultValues: model });
  const formElement = useRef(null);
  const inlineFilters = getInlineFilters();
  const inlineFilterComponents = useMemo(() => inlineFilters.map(getFilterComponent), [expanded]);
  const popupFilters = getPopupFilters();
  const popupFilterComponents = useMemo(() => popupFilters?.map(getFilterComponent), [expanded]);
  useEffect(() => {
    function handleFormChange() {
      if (mode === "update-on-change") {
        setTimeout(() => {
          const values = form.getValues();
          handleSubmit(values);
        }, 0);
      }
    }
    formElement.current?.addEventListener("change", handleFormChange);
    return () => {
      formElement.current?.removeEventListener("change", handleFormChange);
    };
  }, []);
  const modelStringified = JSON.stringify(model);
  useEffect(() => {
    if (!equal(model, form.getValues()) && !form.formState.isDirty) {
      form.reset(model);
    }
  }, [modelStringified]);
  function isRenderedInPortal(filters) {
    return typeof filters[0] !== "string";
  }
  function getFilterName(filter) {
    return typeof filter === "string" ? filter : filter.filterName;
  }
  function handleExpand() {
    setExpanded(true);
  }
  function handleCollapse() {
    setExpanded(false);
  }
  function handleSubmit(model2) {
    form.reset(model2);
    if (onChange) {
      onChange(model2);
    }
  }
  function handleReset() {
    const resetModel = defaultFilter ?? allFilters.reduce((acc, filter) => {
      setObjectFieldByPath(acc, filter.modelField, null);
      return acc;
    }, {});
    handleSubmit(resetModel);
  }
  function getInlineFilters() {
    return isMobile ? getInlineFiltersMobile() : availableFilters;
  }
  function getInlineFiltersMobile() {
    let res = [];
    if (isRenderedInPortal(availableFilters)) {
      res = availableFilters.filter((f) => !allFilters.find((af) => af.filterName === f.filterName)?.collapseOnMobile);
    } else {
      res = availableFilters.filter((f) => !allFilters.find((af) => af.filterName === f)?.collapseOnMobile);
    }
    return res;
  }
  function getPopupFilters() {
    return isMobile ? availableFilters.map(getFilterName).filter((f) => allFilters.find((af) => af.filterName === f)?.collapseOnMobile) : [];
  }
  function getFilterComponent(filter, index) {
    const isHidden = !expanded && index >= maxVisibleItemsCountDesktop;
    const filterName = getFilterName(filter);
    const { component, modelField, componentMobile } = allFilters.find((f) => f.filterName === filterName);
    const FilterComponent = isMobile && componentMobile ? componentMobile : component;
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: cx(classes.filterGroupContainerFilter, { [classes.filterGroupContainerFilterHidden]: isHidden }),
        key: filterName
      },
      /* @__PURE__ */ React.createElement(
        Controller,
        {
          render: ({ field }) => /* @__PURE__ */ React.createElement(
            FilterComponent,
            {
              key: field.name,
              value: field.value,
              onChange: (e) => {
                formElement.current?.dispatchEvent(new Event("change"));
                return field.onChange(e);
              },
              mode
            }
          ),
          name: modelField,
          control: form.control
        }
      )
    );
  }
  const isResetVisible = useMemo(() => !equal(defaultFilter, model), [modelStringified]);
  const buttons = /* @__PURE__ */ React.createElement(
    FilterGroupButtons,
    {
      isFilterChanged: form.formState.isDirty,
      hasExpandButton: isMobile ? popupFilters.length > 0 : availableFilters.length > maxVisibleItemsCountDesktop,
      hasSubmitButton: mode === "update-on-submit",
      isExpanded: expanded,
      onCollapse: handleCollapse,
      onExpand: handleExpand,
      onReset: withResetButton && isResetVisible && handleReset,
      isDataLoading,
      isMobile
    }
  );
  const isPortals = isRenderedInPortal(inlineFilters);
  const isRefsLoaded = isPortals ? inlineFilters.every((f) => f.node) : true;
  const portals = useMemo(() => {
    const mapper = {};
    if (isPortals && isRefsLoaded) {
      inlineFilters.forEach((f, i) => {
        const key = f.nodeId;
        mapper[key] = mapper[key] ? [...mapper[key], inlineFilterComponents[i]] : [inlineFilterComponents[i]];
        if (i === inlineFilters.length - 1) {
          mapper[key].push(buttons);
        }
      });
    }
    return mapper;
  }, [expanded, isRefsLoaded]);
  return /* @__PURE__ */ React.createElement("form", { ref: formElement, className: cx(!isPortals && classes.filterGroupRootContainer), onSubmit: form.handleSubmit(handleSubmit) }, isPortals ? Object.keys(portals).map(
    (key) => createPortal(
      /* @__PURE__ */ React.createElement(
        FiltersContainer,
        {
          viewMode,
          withSubmit: mode === "update-on-submit",
          groupContainerFullWidth
        },
        portals[key]
      ),
      inlineFilters.find((vf) => vf.nodeId === key).node
    )
  ) : /* @__PURE__ */ React.createElement(
    FiltersContainer,
    {
      viewMode,
      withSubmit: mode === "update-on-submit",
      groupContainerFullWidth
    },
    inlineFilterComponents,
    buttons
  ), isMobile && expanded ? /* @__PURE__ */ React.createElement(MultiStepFilterDrawer, { isOpen: expanded, onClose: handleCollapse, onOpen: handleExpand }, popupFilterComponents) : null);
}
export {
  FilterGroupNew
};
//# sourceMappingURL=FilterGroupNew.js.map
